import dayjs from "dayjs";

import { serverResponseFormat, shortMonthAndFullYear } from "@similarweb/pro-logic-components";

import { keywordGroupsQueryClient } from "app/services/keyword-groups/utils/keywordGroupsQueryClient";
import { SourceTypeMap } from "app/UtilitiesAndConstants/Constants/SourceTypes";
import type { IDurationObject } from "app/services/DurationService";
import { DefaultFetchService } from "app/services/fetchService";
import { DEVICES_TYPES_FOR_API, EDeviceType } from "app/UtilitiesAndConstants/Constants/DevicesTypes";
import { granularityNameToMomentUnitSingular } from "app/UtilitiesAndConstants/Constants/Moment";

import { FULFILLED_STATUS } from "app/common/keyword-research/keywords-overview/keywordsOverviewPageConsts";
import type { ApiParams } from "app/common/keyword-research/keywords-overview/keywordsOverviewPageTypes";

import {
    CLICKS_ENDPOINT,
    VOLUME_AND_NO_CLICKS_ENDPOINT,
} from "app/common/keyword-research/keywords-overview/volumeAndClicksConsts";
import type {
    IClicksData,
    IClicksResponseData,
    IClicksRowData,
    IDeviceTraffic,
    IMonthlyData,
    IVolumeAndNoClicksResponseData,
    IVolumeAndNoClicksRowData,
} from "app/common/keyword-research/keywords-overview/volumeAndClicksTypes";

const fetchService = DefaultFetchService.getInstance();

const calculateChange = (currentValue: number, previousValue: number) => (currentValue - previousValue) / previousValue;

const getMonthDataByWebsource = (
    webSource: EDeviceType | DEVICES_TYPES_FOR_API.MOBILE,
    currentMonthData: IDeviceTraffic,
): number => {
    if (!currentMonthData) {
        return;
    }
    if (webSource === EDeviceType.TOTAL) {
        return currentMonthData.Desktop + currentMonthData.Mobile;
    } else {
        return currentMonthData[webSource];
    }
};

const getClicksValueAndChange = (
    rawData: IClicksData,
    webSource: EDeviceType,
    duration: dayjs.Dayjs,
    getChange = true,
): { currentMonthValue: number; change: number } => {
    const monthToVisitsData = rawData.MonthToVisits;
    const webSourceKey = webSource === EDeviceType.MOBILE ? DEVICES_TYPES_FOR_API.MOBILE : webSource;

    const previousMonth = duration.subtract(1, "month").format(serverResponseFormat);

    const currentMonthData = rawData.TotalClicks;
    const previousMonthData = getMonthDataByWebsource(webSourceKey, monthToVisitsData[previousMonth]);

    const change = getChange && previousMonthData && calculateChange(currentMonthData, previousMonthData);
    return { currentMonthValue: currentMonthData, change };
};

export const transformClicksData = (
    rawData: IClicksResponseData,
    keys: string,
    webSource: EDeviceType,
    duration: dayjs.Dayjs,
    getChange?: boolean,
): IClicksRowData | object => {
    if (!rawData.Data[keys]) {
        return {};
    }
    const { currentMonthValue, change } = getClicksValueAndChange(rawData.Data[keys], webSource, duration, getChange);
    return { clicksRowData: { value: currentMonthValue, change } };
};

const getNoClicksValueAndChange = (
    rawData: IMonthlyData[],
    duration: dayjs.Dayjs,
    isNotWindow = true,
): { currentMonthValue: number; change: number } => {
    let currentMonth: string, previousMonth: string;

    if (isNotWindow) {
        currentMonth = duration.format(shortMonthAndFullYear);
        previousMonth = duration.subtract(1, granularityNameToMomentUnitSingular.Monthly).format(shortMonthAndFullYear);
    } else {
        currentMonth = Object.keys(rawData[rawData.length - 1])[0];
        previousMonth = dayjs
            .utc(currentMonth, shortMonthAndFullYear)
            .subtract(1, granularityNameToMomentUnitSingular.Monthly)
            .format(shortMonthAndFullYear);
    }

    const currentMonthValue = rawData.find((dataPoint) => dataPoint[currentMonth])?.[currentMonth];
    const previousMonthValue = rawData.find((dataPoint) => dataPoint[previousMonth])?.[previousMonth];

    const change = previousMonthValue && calculateChange(currentMonthValue, previousMonthValue);
    return { currentMonthValue, change };
};

const getPreviousMonthVolume = (
    websource: EDeviceType,
    previousMonthMobileVolume: number,
    previousMonthAllTrafficVolume: number,
) => {
    switch (websource) {
        case EDeviceType.MOBILE:
            return previousMonthMobileVolume;
        case EDeviceType.DESKTOP:
            return previousMonthAllTrafficVolume - previousMonthMobileVolume;
        default:
            return previousMonthAllTrafficVolume;
    }
};

const getVolumeChange = (
    currentMonthVolume: number,
    rawAllTrafficVolumeData: IMonthlyData[],
    rawMobileShareData: IMonthlyData[],
    duration: dayjs.Dayjs,
    webSource: EDeviceType,
): number => {
    const previousMonth = duration.subtract(1, "month").format(shortMonthAndFullYear);
    const previousMonthAllTrafficVolume = rawAllTrafficVolumeData.find((dataPoint) => dataPoint[previousMonth])?.[
        previousMonth
    ];
    const previousMonthMobileShare = rawMobileShareData.find((dataPoint) => dataPoint[previousMonth])?.[previousMonth];

    if (!previousMonthAllTrafficVolume || !previousMonthMobileShare) {
        return;
    }

    const previousMonthMobileVolume = previousMonthAllTrafficVolume * previousMonthMobileShare;

    const previousMonthVolume = getPreviousMonthVolume(
        webSource,
        previousMonthMobileVolume,
        previousMonthAllTrafficVolume,
    );

    return previousMonthVolume && calculateChange(currentMonthVolume, previousMonthVolume);
};

export const transformVolumeAndNoClicksData = (
    rawData: IVolumeAndNoClicksResponseData,
    keys: string,
    duration: dayjs.Dayjs,
    webSource: EDeviceType,
    isNotWindow?: boolean,
): IVolumeAndNoClicksRowData | object => {
    if (!rawData.Data[keys]) {
        return {};
    }
    const innerRawData = rawData.Data[keys];
    const currentMonthVolume = innerRawData.Volume;
    const volumeDataChange =
        isNotWindow &&
        getVolumeChange(
            currentMonthVolume,
            innerRawData.VolumesMonthly,
            innerRawData.MobileShareMonthly,
            duration,
            webSource,
        );
    const zeroClicksData = getNoClicksValueAndChange(innerRawData.ZeroClicksMonthly, duration, isNotWindow);

    return {
        volumeRowData: { value: innerRawData.Volume, change: volumeDataChange },
        zeroClicksRowData: {
            value: zeroClicksData.currentMonthValue,
            change: zeroClicksData.change,
        },
    };
};

export const getApiParams = async (
    isKeywordGroup: boolean,
    keys: string,
    durationForAPI: IDurationObject<string>,
    country: string | number,
    webSource: string,
): Promise<{ clicksParams: ApiParams; volumeAndNoClicksParams: ApiParams }> => {
    const { from, to, isWindow } = durationForAPI;
    const baseParams = {
        to,
        from,
        country,
        isWindow,
        webSource,
        keys,
        sourceType: SourceTypeMap.Total,
        ...(isKeywordGroup && {
            GroupHash: (await keywordGroupsQueryClient.getGroupById(keys))?.GroupHash,
        }),
    };
    const clicksParams = {
        ...baseParams,
        includeSubDomains: false,
        Device: webSource === EDeviceType.MOBILE ? DEVICES_TYPES_FOR_API.MOBILE : webSource,
    };

    const volumeAndNoClicksParams = {
        ...baseParams,
        GetlAllAvailableTrend: true,
    };
    return { clicksParams, volumeAndNoClicksParams };
};

export const fetchData = async (
    volumeAndNoClicksParams: ApiParams,
    clicksParams: ApiParams,
): Promise<{
    volumeAndNoClicksData: IVolumeAndNoClicksResponseData;
    clicksData: IClicksResponseData;
}> => {
    const volumeAndNoClicksPromise = fetchService.get<IVolumeAndNoClicksResponseData>(
        VOLUME_AND_NO_CLICKS_ENDPOINT,
        volumeAndNoClicksParams,
    );
    const clicksPromise = fetchService.get<IClicksResponseData>(CLICKS_ENDPOINT, clicksParams);

    const [volumeAndNoClicksResponse, clicksResponse] = await Promise.allSettled([
        volumeAndNoClicksPromise,
        clicksPromise,
    ]);

    const [volumeAndNoClicksData, clicksData] = [volumeAndNoClicksResponse, clicksResponse].map(
        (response) => response.status === FULFILLED_STATUS && response.value,
    );
    return {
        volumeAndNoClicksData: volumeAndNoClicksData as IVolumeAndNoClicksResponseData,
        clicksData: clicksData as IClicksResponseData,
    };
};
